import { useState, useId, useRef } from 'react'
import { useFetcher } from 'react-router'

import { Card, CardContent } from '#app/components/ui/card.tsx'
import { CopyButton } from '#app/components/ui/copy-button.tsx'
import { formatBytes } from '#app/components/ui/dropzone.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { type MediaItem } from '#app/hooks/use-supabase-upload.ts'

// Schema kept for reference but not used in controlled form approach
// const MediaUpdateSchema = z.object({
// 	id: z.string(),
// 	title: z.string().optional(),
// 	altText: z.string().optional(),
// 	description: z.string().optional(),
// })

interface MediaEditPanelProps {
	media: MediaItem
	open: boolean
	onOpenChange: (open: boolean) => void
	onUpdate: (updatedMedia: MediaItem) => void
}

export function MediaEditPanel({
	media,
	open,
	onOpenChange,
	onUpdate,
}: MediaEditPanelProps) {
	const fetcher = useFetcher<{ success: boolean; media?: MediaItem }>()
	const formId = useId()

	// Controlled form state
	const [title, setTitle] = useState(media.title || '')
	const [altText, setAltText] = useState(media.altText || '')
	const [description, setDescription] = useState(media.description || '')

	const isSubmitting = fetcher.state !== 'idle'

	// Handle form submission
	const handleSubmit = () => {
		const formData = new FormData()
		formData.append('intent', 'update-media')
		formData.append('id', media.id)
		formData.append('title', title)
		formData.append('altText', altText)
		formData.append('description', description)

		void fetcher.submit(formData, {
			method: 'post',
			action: '/dashboard/media',
		})
	}

	// Handle successful response - React Router v7 way with microtask deferral
	const wasSubmitting = useRef(false)
	if (fetcher.state === 'submitting') {
		wasSubmitting.current = true
	}

	if (
		wasSubmitting.current &&
		fetcher.state === 'idle' &&
		fetcher.data?.success &&
		fetcher.data.media
	) {
		wasSubmitting.current = false
		// Defer state updates to avoid render-time updates
		const mediaData = fetcher.data.media
		queueMicrotask(() => {
			if (mediaData) {
				onUpdate(mediaData)
				onOpenChange(false)
			}
		})
	}

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>Edit Media Details</SheetTitle>
					<SheetDescription>
						Update the title, alt text, and description for this media file.
					</SheetDescription>
				</SheetHeader>

				<div className="mb-6 space-y-4">
					{/* Preview */}
					<div className="flex justify-center">
						{media.mimeType?.startsWith('image/') ? (
							<img
								src={media.url || ''}
								alt={media.altText || media.name || ''}
								className="w-full rounded-lg object-contain"
							/>
						) : (
							<div className="bg-accent flex h-48 w-full items-center justify-center rounded-lg">
								<Icon name="file" className="size-12 opacity-60" />
							</div>
						)}
					</div>

					{/* Edit Form */}
					<Card>
						<CardContent className="p-4">
							<div className="space-y-4">
								<div>
									<label
										htmlFor={`title-${formId}`}
										className="text-sm font-medium"
									>
										Title
									</label>
									<input
										id={`title-${formId}`}
										type="text"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										placeholder="Enter a title..."
										className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
									/>
								</div>

								<div>
									<label
										htmlFor={`altText-${formId}`}
										className="text-sm font-medium"
									>
										Alt Text
									</label>
									<input
										id={`altText-${formId}`}
										type="text"
										value={altText}
										onChange={(e) => setAltText(e.target.value)}
										placeholder="Describe the image for accessibility..."
										className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
									/>
								</div>

								<div>
									<label
										htmlFor={`description-${formId}`}
										className="text-sm font-medium"
									>
										Description
									</label>
									<textarea
										id={`description-${formId}`}
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										placeholder="Add a description..."
										rows={3}
										className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
									/>
								</div>

								<div className="flex">
									<StatusButton
										status={isSubmitting ? 'pending' : 'idle'}
										type="button"
										onClick={handleSubmit}
										disabled={isSubmitting}
										className="w-full"
									>
										<Icon name="check" className="mr-2 size-4" />
										Save Changes
									</StatusButton>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* File Details */}
					<Card>
						<CardContent className="p-4">
							<div className="space-y-3">
								<h4 className="font-medium">File Details</h4>
								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<p className="text-muted-foreground">Name</p>
										<p className="truncate font-medium">{media.name}</p>
									</div>
									<div>
										<p className="text-muted-foreground">Type</p>
										<p className="font-medium">{media.mimeType}</p>
									</div>
									<div>
										<p className="text-muted-foreground">Path</p>
										<p className="font-medium">{media.path || 'Root'}</p>
									</div>
									{media.size && (
										<div>
											<p className="text-muted-foreground">Size</p>
											<p className="font-medium">{formatBytes(media.size)}</p>
										</div>
									)}
									<div className="col-span-2">
										<p className="text-muted-foreground mb-2">URL</p>
										<CopyButton textToCopy={media.url || ''} />
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</SheetContent>
		</Sheet>
	)
}
